import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import { accountingCurrencyFormatter } from "@common/helpers/currency";
import { ScheduleApplicationRelatedLink } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { InvoiceViewOptionGroup } from "@common/types/invoice";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { Menu } from "@design-system/Menu/Menu.lib";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import {
    EntityAvatarGroup,
    EntityAvatarGroupEntity,
} from "@components/avatar/EntityAvatarGroup";
import { MatchCountChip } from "@components/accounting/transactionMatch/MatchCountChip";
import { MAX_VISIBLE_MATCH_ENTITY_ICONS } from "@components/accounting/transactionMatch/matchCell";
import matchCellStyles from "@components/accounting/transactionMatch/MatchCell.module.scss";
import { formatIsoDateForDisplay } from "@helpers/date";
import { useOverlays } from "contexts/OverlayContext";
import {
    collectRelatedLinkEntityIds,
    formatRelatedLinksCount,
    getRelatedLinkGoToLabel,
} from "../scheduleApplicationMatchCopy";
import {
    ScheduleApplicationParentRow,
    ScheduleApplicationRow,
} from "../scheduleApplicationRowHelpers";
import {
    ScheduleApplicationsTableCallbacks,
    useScheduleApplicationsTableMeta,
} from "../scheduleApplicationsTableMeta";
import styles from "./ScheduleApplicationMatchCell.module.scss";

function describeLink(link: ScheduleApplicationRelatedLink): string {
    const parts = [link.label];
    if (link.date) {
        parts.push(formatIsoDateForDisplay(link.date));
    }
    parts.push(accountingCurrencyFormatter.format(link.amount));
    return parts.join(" · ");
}

function relatedLinkKey(link: ScheduleApplicationRelatedLink): string {
    return `${link.type}-${link.invoiceId ?? link.billId ?? link.transactionId}`;
}

function openRelatedLink(
    link: ScheduleApplicationRelatedLink,
    callbacks: ScheduleApplicationsTableCallbacks | undefined,
) {
    if (link.type === "invoice" && link.invoiceId) {
        callbacks?.onOpenInvoice({
            invoiceId: link.invoiceId,
            groupBy: InvoiceViewOptionGroup.INVOICE,
        });
        return;
    }
    if (link.type === "bill" && link.billId) {
        callbacks?.onOpenBill(link.billId);
        return;
    }
    if (link.type === "transaction" && link.transactionId != null) {
        callbacks?.onOpenTransaction({
            transactionId: link.transactionId,
            entityId: link.entityId,
        });
    }
}

export interface ScheduleApplicationMatchCellContentProps {
    row: ScheduleApplicationRow;
    entitiesById?: ReadonlyMap<number, EntityAvatarGroupEntity>;
    callbacks?: ScheduleApplicationsTableCallbacks;
}

export const ScheduleApplicationMatchCellContent = ({
    row,
    entitiesById,
    callbacks,
}: ScheduleApplicationMatchCellContentProps) => {
    const { openOverlay } = useOverlays();
    const parentRow: ScheduleApplicationParentRow | null =
        row.rowType === "application" ? row : null;
    const matchTarget = parentRow?.matchTarget ?? null;
    const relatedLinks = parentRow?.relatedLinks ?? [];

    const handleOpenMatch = useCallback(() => {
        if (!matchTarget) {
            return;
        }
        if ("transactionId" in matchTarget) {
            openOverlay("transactionMatch", {
                transactionId: matchTarget.transactionId,
            });
            return;
        }
        openOverlay("documentAccrualMatch", {
            documentType: matchTarget.documentType,
            documentId: matchTarget.documentId,
        });
    }, [matchTarget, openOverlay]);

    if (!matchTarget) {
        return <ViewTableCell />;
    }

    if (relatedLinks.length === 0) {
        return (
            <ViewTableCell captureClicks className={matchCellStyles.cell}>
                <EditableCellContent isEmpty onClick={handleOpenMatch} />
            </ViewTableCell>
        );
    }

    const entities = collectRelatedLinkEntityIds(relatedLinks).flatMap(
        (entityId) => {
            const entity = entitiesById?.get(entityId);
            return entity ? [entity] : [];
        },
    );
    const [firstLink] = relatedLinks;

    return (
        <ViewTableCell captureClicks className={matchCellStyles.cell}>
            <div className={styles.content}>
                <EditableCellContent
                    className={matchCellStyles.match}
                    onClick={handleOpenMatch}
                    tooltip={relatedLinks.map((link) => (
                        <div key={relatedLinkKey(link)}>
                            {describeLink(link)}
                        </div>
                    ))}
                >
                    {entities.length > 0 && (
                        <EntityAvatarGroup
                            entities={entities}
                            max={MAX_VISIBLE_MATCH_ENTITY_ICONS}
                        />
                    )}
                    <span className={styles.label}>{firstLink.label}</span>
                    <span className={matchCellStyles.countChip}>
                        <MatchCountChip
                            label={formatRelatedLinksCount(relatedLinks)}
                        />
                    </span>
                </EditableCellContent>
                <CaptureClicks>
                    <Menu.More
                        aria-label="Match actions"
                        className={viewTableStyles.showOnHover}
                    >
                        {relatedLinks.map((link) => (
                            <Menu.Item
                                key={relatedLinkKey(link)}
                                description={link.label}
                                onClick={() => openRelatedLink(link, callbacks)}
                            >
                                {getRelatedLinkGoToLabel(link)}
                            </Menu.Item>
                        ))}
                    </Menu.More>
                </CaptureClicks>
            </div>
        </ViewTableCell>
    );
};

export const ScheduleApplicationMatchCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ScheduleApplicationRow, string>) => {
        const meta = useScheduleApplicationsTableMeta(table);
        return (
            <ScheduleApplicationMatchCellContent
                row={row.original}
                entitiesById={meta.entitiesById}
                callbacks={meta.callbacks}
            />
        );
    },
);
ScheduleApplicationMatchCell.displayName = "ScheduleApplicationMatchCell";
